import {
  FormLabel,
  makeStyles,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Accordion,
  AccordionSummary,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { DatePicker } from '@material-ui/pickers';
import React, { useState } from 'react';
import useSWR from 'swr';

import { Colors } from 'src/common/ui/base';
import { Alert } from 'src/common/ui/components';
import { WindowDisplay } from 'src/dropInClinic/WindowDisplay';
import { deserializeAvailabilityGraphql } from 'src/dropInClinic/deserializeGraphql';
import { AVAILABILITY_QUERY } from 'src/dropInClinic/queries';
import { AvailabilityQueryReturn, Overview, ProviderAvailability } from 'src/dropInClinic/types';

const StateAvailability = ({
  state,
  overview,
  providerAvailability,
}: {
  state: string;
  overview: Overview;
  providerAvailability: ProviderAvailability[];
}) => {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(false);

  return (
    <Accordion
      expanded={expanded}
      key={state}
      className={overview.hasAvailability ? '' : classes.noAvailability}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} onClick={() => setExpanded(!expanded)}>
        <b>{state}</b>{' '}
        {overview.windows.map(window => (
          <WindowDisplay window={window} />
        ))}
      </AccordionSummary>
      <List dense>
        {providerAvailability.map(availability => (
          <AvailabilityItem
            dropInProvider={availability.dropInProvider}
            availability={availability.availability}
            key={`${availability.dropInProvider.lastName}-${availability.availability.start}`}
          />
        ))}
      </List>
    </Accordion>
  );
};

const AvailabilityItem = ({ dropInProvider, availability }: ProviderAvailability) => {
  return (
    <ListItem>
      <ListItemText>
        {dropInProvider.firstName} {dropInProvider.lastName}
      </ListItemText>
      <ListItemSecondaryAction>
        <WindowDisplay window={availability} />
      </ListItemSecondaryAction>
    </ListItem>
  );
};

export const ProviderAvailabilities = () => {
  const [searchDate, setSearchDate] = useState(new Date());
  const { data, error } = useSWR<AvailabilityQueryReturn>([
    AVAILABILITY_QUERY,
    { date: searchDate },
  ]);

  const classes = useStyles();

  if (!data) {
    return <div className={classes.root}>Fetching availability</div>;
  }

  const availabilityByState = data.getClinicAvailabilitiesByDate
    ? deserializeAvailabilityGraphql(data.getClinicAvailabilitiesByDate)
    : {};

  return (
    <div className={classes.root}>
      {error && <Alert title="Error fetching availability" error={error} severity="error" />}
      <span className={classes.header}>
        <FormLabel>Pop-In Provider Availability</FormLabel>
        <DatePicker label="Search Availability" value={searchDate} onChange={setSearchDate} />
      </span>
      {Object.entries(availabilityByState).map(([state, stateAvailability]) => (
        <StateAvailability
          state={state}
          overview={stateAvailability.overview}
          providerAvailability={stateAvailability.providerAvailability}
        />
      ))}
    </div>
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3),
    width: 500,
  },
  noAvailability: {
    backgroundColor: Colors.Coral2,
  },
  stateSection: {
    marginTop: 10,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    paddingBottom: theme.spacing(3),
  },
}));
